/**
 * Customers Service
 *
 * Data access layer for customer data derived from bookings.
 * Customers are stored in the bookings table (denormalized);
 * this service provides aggregated, deduplicated views.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export interface CustomerRecord {
  customer_firstname: string;
  customer_lastname: string;
  customer_email: string;
  customer_phone: string;
  language: string | null;
  marketing_consent: boolean | null;
  total_bookings: number;
  total_spent: number;
  first_booking_at: string | null;
  last_booking_at: string | null;
}

/**
 * Fetch unique customers for a merchant via the get_customers RPC.
 */
export async function getCustomers(
  merchantId: string,
  filters?: {
    searchQuery?: string;
    marketingConsent?: boolean | null;
    limit?: number;
    offset?: number;
  }
) {
  try {
    const { data, error } = await supabase.rpc('get_customers', {
      p_merchant_id: merchantId,
      p_query: filters?.searchQuery || null,
      p_marketing_consent: filters?.marketingConsent ?? null,
      p_limit: filters?.limit ?? 100,
      p_offset: filters?.offset ?? 0,
    });

    if (error) throw error;

    return { data: (data || []) as CustomerRecord[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Export customers to CSV string
 */
export function exportCustomersCsv(customers: CustomerRecord[], currencyCode?: string): string {
  const headers = [
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Language',
    'Marketing Consent',
    'Total Bookings',
    `Total Spent (${currencyCode || 'EUR'})`,
    'First Booking',
    'Last Booking',
  ];

  const escapeField = (value: string) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const rows = customers.map((c) => [
    escapeField(c.customer_firstname),
    escapeField(c.customer_lastname),
    escapeField(c.customer_email),
    escapeField(c.customer_phone),
    c.language || '',
    c.marketing_consent === null ? 'N/A' : c.marketing_consent ? 'Yes' : 'No',
    String(c.total_bookings),
    (c.total_spent || 0).toFixed(2),
    c.first_booking_at ? new Date(c.first_booking_at).toLocaleDateString() : '',
    c.last_booking_at ? new Date(c.last_booking_at).toLocaleDateString() : '',
  ]);

  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
}
